import { type ReactNode } from "react";
import { IconWarning, type IconProps } from "@boligportal/bricks-icons";
import { type ButtonProps, cn } from "@boligportal/bricks-ui";

type Sizes = "sm" | "md" | "lg";

type Props = {
  /**
   * classname passed to the wrapper
   */
  className?: string;
  /** The size of the component. */
  size?: Sizes;
  /**
   * The icon to display. Defaults to Alert Triangle with red "danger" color.
   * @example
   * // Use a different icon
   * <AlertState icon={IconAlertCircle} />
   * @example
   * // Use a different icon with a different color
   * <AlertState icon={({ className }) => <IconCheckmarkCircle className={cn(className, "text-success") />} />
   */
  icon?: React.ComponentType<IconProps>;
  /** The headline text to display. */
  headline?: ReactNode;
  /** The description text to display. */
  description?: ReactNode;
  /**
   * Render function for the primary action button, which provides button props for `size` and `variant`, to display the button correctly.
   * @example
   * // This is how you should use it
   * <AlertState renderPrimaryAction={(props) => <Button {...props}>Retry</Button>} />
   * // Explicit example
   * <AlertState renderPrimaryAction={({size, variant}) => <Button size={size} variant={variant}>Retry</Button>} />
   */
  renderPrimaryAction?: (
    props: Pick<ButtonProps, "size" | "color" | "variant">,
  ) => ReactNode;
  /**
   * Render function for the secondary action button, which provides button props for `size` and `variant`, to display the button correctly.
   * @example
   * <AlertState renderSecondaryAction={(props) => <Button {...props}>Cancel</Button>} />
   */
  renderSecondaryAction?: (
    props: Pick<ButtonProps, "size" | "color" | "variant">,
  ) => ReactNode;
};

const sizeConfig: Record<
  Sizes,
  {
    buttonSize: ButtonProps["size"];
    classNames: {
      wrapper: string;
      icon: string;
      headlineWrapper: string;
      headline: string;
      description: string;
    };
  }
> = {
  sm: {
    classNames: {
      wrapper: "w-68.25",
      icon: "size-[40px] text-[40px]",
      headlineWrapper: "gap-1",
      headline: "text-sm font-bold",
      description: "text-xs",
    },
    buttonSize: "xs",
  },
  md: {
    classNames: {
      wrapper: "w-79.5",
      icon: "size-[72px] text-[72px]",
      headlineWrapper: "gap-2",
      headline: "text-xl",
      description: "text-base",
    },
    buttonSize: "sm",
  },
  lg: {
    classNames: {
      wrapper: "w-79.5",
      icon: "size-[96px] text-[96px]",
      headlineWrapper: "gap-2",
      headline: "text-3xl",
      description: "text-base",
    },
    buttonSize: "md",
  },
};

/**
 * The `AlertState` component displays a message, icon, and action buttons.
 *
 * @example
 * <AlertState
 *   size="md"
 *   headline="No Data"
 *   description="There is no data to display."
 *   renderPrimaryAction={(props) => <Button {...props} leadingIcon={IconRefreshOutline}>Retry</Button>}
 *   renderSecondaryAction={(props) => <Button {...props}>Cancel</Button>}
 * />
 *
 * @example
 * // Example with different icon and icon color
 * <AlertState
 *   size="md"
 *   headline="Success"
 *   description="Everything went according to plan."
 *   icon={({ className }) => <IconCheckmarkCircle className={cn(className, "text-success") />}
 * />
 *
 * @example
 * // Example with Link in actions
 * <AlertState
 *   size="md"
 *   headline="No Data"
 *   description="There is no data to display."
 *   icon={({ className }) => <IconWarning className={cn(className, "text-danger") />}
 *   renderPrimaryAction={(props) => <Link href="/somelink">
 *      <Button {...props}>Retry</Button>
 *    </Link>}
 *   renderSecondaryAction={(props) => <Button {...props}>Cancel</Button>}
 * />
 */
export function AlertState({
  size = "md",
  description,
  headline,
  icon: IconComponent = IconWarning,
  renderPrimaryAction,
  renderSecondaryAction,
  className,
}: Props) {
  const { classNames: sizeClasses, buttonSize } = sizeConfig[size];

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-4",
        sizeClasses.wrapper,
        className,
      )}
    >
      {!!IconComponent && (
        <IconComponent className={cn("text-danger", sizeClasses.icon)} />
      )}
      <div
        className={cn(
          "flex flex-col items-center text-center",
          sizeClasses.headlineWrapper,
        )}
      >
        {headline && <div className={sizeClasses.headline}>{headline}</div>}
        {description && (
          <div className={cn("text-muted", sizeClasses.description)}>
            {description}
          </div>
        )}
      </div>
      {(renderPrimaryAction ?? renderSecondaryAction) && (
        <div className="flex flex-col items-center gap-2">
          {renderPrimaryAction?.({
            size: buttonSize,
            color: "primary-administration",
            variant: "solid",
          })}
          {renderSecondaryAction?.({
            size: buttonSize,
            color: "primary-administration",
            variant: "ghost",
          })}
        </div>
      )}
    </div>
  );
}
